import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, ExternalLink, SlidersHorizontal } from "lucide-react";
import {
  Pill, EmptyState, Loading, ErrorState, TableShell, Th, Pagination,
  Drawer, ConfirmModal, ExportButton, Card, KV,
} from "./AdminUI";

const FILTERS = [
  ["all", "All"], ["client", "Clients"], ["pro", "Professionals"],
  ["pro_plus", "Pro Plus"], ["free_pro", "Free Pro"], ["trial", "Trial"],
  ["verified", "Verified"], ["unverified", "Unverified"],
  ["suspended", "Suspended"], ["deletion_pending", "Deletion pending"],
  ["archived", "Archived"],
];
const PRIMARY = ["all", "client", "pro", "pro_plus", "suspended"];
const PERIODS = [["", "Any time"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]];

const STATE_KEY = "limya_admin_users_state";

export default function UsersTab() {
  const saved = (() => { try { return JSON.parse(localStorage.getItem(STATE_KEY)) || {}; } catch { return {}; } })();
  const [filter, setFilter] = useState(saved.filter || "all");
  const [q, setQ] = useState(saved.q || "");
  const [period, setPeriod] = useState(saved.period || "");
  const [page, setPage] = useState(saved.page || 1);
  const [showMore, setShowMore] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    localStorage.setItem(STATE_KEY, JSON.stringify({ filter, q, period, page }));
  }, [filter, q, period, page]);

  const load = useCallback(() => {
    setError(null);
    const params = { page, page_size: 25 };
    if (filter !== "all") params.role = filter;
    if (filter === "archived") params.include_archived = true;
    if (q.trim()) params.q = q.trim();
    if (period) params.created_since = new Date(Date.now() - Number(period) * 864e5).toISOString();
    api.get("/admin/users", { params })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message));
  }, [filter, q, period, page]);

  useEffect(() => { setPage(1); }, [filter, period]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  return (
    <div data-testid="admin-users-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-black/10 rounded-full px-3 py-2 flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="text-[#222]/40" />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="Name, email, business name or account ID"
            className="flex-1 outline-none text-sm bg-transparent min-w-0" data-testid="admin-users-search" />
        </div>
        {PRIMARY.map(k => {
          const label = FILTERS.find(f => f[0] === k)[1];
          return (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === k ? "bg-[#222] text-white" : "bg-white border border-black/10"}`}
              data-testid={`admin-users-filter-${k}`}>{label}</button>
          );
        })}
        <button onClick={() => setShowMore(v => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${showMore ? "bg-[#222] text-white" : "bg-white border border-black/10"}`}
          data-testid="admin-users-more-filters">
          <SlidersHorizontal size={12} /> Filters
        </button>
        <div className="ml-auto"><ExportButton kind="users" label="Users" /></div>
      </div>

      {showMore && (
        <div className="bg-white rounded-2xl border border-black/10 p-4 mb-4 flex flex-wrap gap-2 items-center"
          data-testid="admin-users-filter-drawer">
          {FILTERS.filter(f => !PRIMARY.includes(f[0])).map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === k ? "bg-[#222] text-white" : "bg-black/5"}`}
              data-testid={`admin-users-filter-${k}`}>{label}</button>
          ))}
          <span className="w-px h-5 bg-black/10 mx-1" />
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-black/5 outline-none"
            data-testid="admin-users-period">
            {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}

      {error ? <ErrorState error={error} onRetry={load} />
        : !data ? <Loading />
        : data.items.length === 0 ? (
          <EmptyState title="No accounts match" body="Try a different filter, period or search term."
            testid="admin-users-empty" />
        ) : (
          <>
            <TableShell testid="admin-users-table">
              <thead>
                <tr>
                  <Th>Account</Th>
                  <Th className="hidden md:table-cell">Email</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th className="hidden lg:table-cell">Joined</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(u => {
                  const isPro = u.role === "pro";
                  return (
                    <tr key={u.id} className="border-t border-black/5 hover:bg-black/[0.015]"
                      data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium truncate max-w-[220px]">{u.name}</p>
                        {isPro && u.pro_profile?.business_name && (
                          <p className="text-xs text-[#222]/50 truncate max-w-[220px]">{u.pro_profile.business_name}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#222]/60 hidden md:table-cell truncate max-w-[200px]">{u.email}</td>
                      <td className="px-4 py-3">
                        <Pill tone={u.role === "admin" ? "dark" : isPro && u.is_pro_plus ? "green" : "neutral"}>
                          {u.role === "admin" ? "Admin" : u.role === "client" ? "Client" : u.is_pro_plus ? "Pro Plus" : "Free Pro"}
                        </Pill>
                      </td>
                      <td className="px-4 py-3 space-x-1">
                        {u.deleted ? <Pill tone="dark">Deleted</Pill>
                          : u.deletion_pending ? <Pill tone="red">Deletion pending</Pill>
                          : u.archived ? <Pill tone="red">Archived</Pill>
                          : u.suspended ? <Pill tone="amber">Suspended</Pill>
                          : <Pill tone="green">Active</Pill>}
                        {isPro && u.verified && <Pill tone="blue">Verified</Pill>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#222]/50 hidden lg:table-cell whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {isPro && (
                          <a href={`/pro/${u.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#245544] mr-2">
                            <ExternalLink size={12} />
                          </a>
                        )}
                        <button onClick={() => setOpenId(u.id)}
                          className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
                          data-testid={`admin-user-open-${u.id}`}>Manage</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </TableShell>
            <Pagination page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage}
              testid="admin-users-pagination" />
          </>
        )}

      {openId && <UserDrawer userId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function UserDrawer({ userId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => api.get(`/admin/users/${userId}`).then(r => setD(r.data)), [userId]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body = {}) => {
    try {
      await api.post(`/admin/users/${userId}/${path}`, body);
      toast.success("Done — recorded in the audit log");
      await load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!d) return <Drawer title="Account" onClose={onClose}><Loading /></Drawer>;
  const isPro = d.role === "pro";

  const ACTIONS = {
    suspend: {
      title: "Suspend this account",
      impact: "The account can no longer sign in and any public profile is removed from discovery and search. "
        + "Nothing is deleted — posts, reviews, messages and billing state all stay exactly as they are. "
        + "The profile URL keeps returning a neutral \"temporarily unavailable\" message. Reversible at any time.",
      run: r => act("suspend", { reason: r }), danger: true, label: "Suspend account",
    },
    archive: {
      title: "Archive this account",
      impact: "Login is blocked and the account is removed from every public area and from default admin lists. "
        + "This is non-destructive: no content is deleted and the account can be restored from archive.",
      run: r => act("archive", { reason: r }), danger: true, label: "Archive account",
    },
    hide: {
      title: "Hide this public profile",
      impact: "The Professional's profile stops appearing publicly, but the account keeps working normally — "
        + "they can still sign in, message and manage their profile. Nothing is deleted.",
      run: r => act("hide-profile", { reason: r }), danger: true, label: "Hide profile",
    },
    revoke: {
      title: "Revoke all sessions",
      impact: "Every token issued to this account stops working immediately, so they are signed out everywhere. "
        + "The account is NOT suspended — they can sign back in with their password right away.",
      run: r => act("revoke-sessions", { reason: r }), danger: true, label: "Revoke sessions",
    },
  };

  return (
    <Drawer title={d.name} subtitle={d.email} onClose={onClose} testid="admin-user-drawer">
      <Card title="Operational snapshot" testid="drawer-snapshot">
        <KV label="Account ID" value={d.id} mono />
        <KV label="Role" value={d.role} />
        <KV label="Joined" value={d.created_at ? new Date(d.created_at).toLocaleString() : "—"} />
        <KV label="Account status" value={d.archived ? "Archived" : d.suspended ? "Suspended" : "Active"} />
        {isPro && <KV label="Membership" value={d.billing?.is_pro_plus ? `Pro Plus (${d.billing.pro_plus_status || "active"})` : "Free Pro"} />}
        {isPro && d.pro_profile && <KV label="Verified Profile" value={d.pro_profile.verified ? "Yes" : "No"} />}
        <KV label="Email verified" value={d.security?.email_verified ? "Yes" : "No"} />
        <KV label="MFA" value={d.security?.mfa_enabled ? "Enabled" : "Not enabled"} />
        <KV label="Session generation" value={d.security?.session_generation} />
        <KV label="Reports against" value={d.reports_against} />
        <KV label="Failed emails" value={d.email_issues} />
      </Card>

      <Card title="Activity" testid="drawer-activity">
        <KV label="Requests created" value={d.activity?.requests_created} />
        <KV label="Wall / profile posts" value={d.activity?.wall_posts} />
        <KV label="Reviews written" value={d.activity?.reviews_written} />
        <KV label="Reviews received" value={d.activity?.reviews_received} />
        <KV label="Reports filed" value={d.activity?.reports_filed} />
        {isPro && <KV label="Pinned by clients" value={d.saved_by_count} />}
        {isPro && <KV label="Followers" value={d.followers_count} />}
      </Card>

      <Card title="Account deletion" testid="drawer-deletion">
        {!d.deletion?.requested ? <p className="text-xs text-[#222]/55">No deletion has been requested.</p> : (
          <>
            <KV label="State" value={d.deletion.state} />
            <KV label="Requested" value={d.deletion.requested_at ? new Date(d.deletion.requested_at).toLocaleString() : "—"} />
            {d.deletion.last_error && <KV label="Last error" value={d.deletion.last_error} />}
            {d.deletion.state !== "completed" && (
              <button onClick={async () => {
                try { await api.post(`/account/deletion/retry/${userId}`); toast.success("Retry attempted"); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
              }} className="mt-3 text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
                data-testid="drawer-retry-deletion">Retry deletion workflow</button>
            )}
          </>
        )}
      </Card>

      {isPro && d.billing && (
        <Card title="Billing (read-only — Stripe is the source of truth)" testid="drawer-billing">
          <KV label="Status" value={d.billing.pro_plus_status || (d.billing.is_pro_plus ? "active" : "free")} />
          <KV label="Access ends" value={d.billing.pro_plus_expires_at?.slice(0, 10) || "—"} />
          <KV label="Cancel at period end" value={d.billing.pro_plus_cancel_at_period_end ? "Yes" : "No"} />
          {d.billing.stripe_customer_url && (
            <a href={d.billing.stripe_customer_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#245544] mt-2">
              Open in Stripe <ExternalLink size={11} />
            </a>
          )}
        </Card>
      )}

      <Card title="Admin actions" testid="drawer-actions">
        <div className="flex flex-wrap gap-2">
          {d.role !== "admin" && (d.suspended
            ? <button onClick={() => act("unsuspend", { reason: "Restored by admin" })}
                className="text-xs px-3 py-1.5 rounded-full bg-[#2E6B57] text-white font-semibold"
                data-testid="drawer-unsuspend">Unsuspend</button>
            : <button onClick={() => setModal("suspend")}
                className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 font-semibold"
                data-testid="drawer-suspend">Suspend</button>)}
          {d.role !== "admin" && (d.archived
            ? <button onClick={() => act("restore-archived", { reason: "Restored by admin" })}
                className="text-xs px-3 py-1.5 rounded-full bg-[#2E6B57] text-white font-semibold"
                data-testid="drawer-restore">Restore from archive</button>
            : <button onClick={() => setModal("archive")}
                className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-700 font-semibold"
                data-testid="drawer-archive">Archive</button>)}
          {isPro && (d.pro_profile?.hidden
            ? <button onClick={() => act("unhide-profile", { reason: "Restored by admin" })}
                className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
                data-testid="drawer-unhide">Unhide profile</button>
            : <button onClick={() => setModal("hide")}
                className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
                data-testid="drawer-hide">Hide profile</button>)}
          <button onClick={() => setModal("revoke")}
            className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
            data-testid="drawer-revoke">Revoke sessions</button>
          {isPro && (
            <button onClick={() => act("feature")} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
              data-testid="drawer-feature">{d.pro_profile?.featured ? "Unfeature" : "Feature"}</button>
          )}
          {isPro && !d.security?.email_verified && (
            <button onClick={async () => {
              try { await api.post(`/admin/verification/${userId}/resend-email`); toast.success("Verification email queued (delivery is mocked)"); }
              catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
              data-testid="drawer-resend-verify">Resend verification email</button>
          )}
        </div>
        <p className="text-[11px] text-[#222]/45 mt-3">{d.privacy_note}</p>
      </Card>

      {d.audit_history?.length > 0 && (
        <Card title="Admin history" testid="drawer-history">
          {d.audit_history.map(a => (
            <div key={a.id} className="text-xs border-b border-black/5 py-1.5 last:border-0">
              <b>{a.action}</b> — {a.reason || "no reason given"}
              <span className="text-[#222]/40"> · {new Date(a.created_at).toLocaleDateString()} · {a.admin_email}</span>
            </div>
          ))}
        </Card>
      )}

      {modal && (
        <ConfirmModal title={ACTIONS[modal].title} impact={ACTIONS[modal].impact}
          confirmLabel={ACTIONS[modal].label} danger={ACTIONS[modal].danger}
          onConfirm={ACTIONS[modal].run} onClose={() => setModal(null)} />
      )}
    </Drawer>
  );
}
